import React, { useEffect, useMemo, useState } from 'react';
import { purchaseAPI } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Plus, Trash2, Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };

const lineTotal = (it) => {
  const qty = num(it.quantity);
  const rate = num(it.rate);
  if (it.cts_order && num(it.cts_unit_weight_kg) > 0) {
    return rate * qty * num(it.cts_unit_weight_kg);
  }
  return rate * qty;
};

export function PREditDialog({ pr, onClose, onSaved }) {
  const [lines, setLines] = useState([]);
  const [terms, setTerms] = useState({ delivery_terms: '', delivery_date: '', payment_terms: '', documents_required: '', special_terms: '' });
  const [reason, setReason] = useState('');
  const [allItems, setAllItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [linkWarnings, setLinkWarnings] = useState([]);

  useEffect(() => {
    if (!pr) return;
    setLines((pr.items || []).map((it, i) => ({
      ...it,
      _key: `${it.item_id || 'x'}-${i}`,
      quantity: it.quantity != null ? String(it.quantity) : '',
      rate: it.rate != null ? String(it.rate) : '',
      gst_percent: it.gst_percent != null ? String(it.gst_percent) : '0',
    })));
    setTerms({
      delivery_terms: pr.common_delivery_terms || '',
      delivery_date: pr.common_delivery_date || '',
      payment_terms: pr.common_payment_terms || '',
      documents_required: pr.common_documents_required || '',
      special_terms: pr.common_special_terms || '',
    });
    setReason('');
    purchaseAPI.getItems().then(res => setAllItems(res.data || [])).catch(() => setAllItems([]));
  }, [pr]);

  const itemOptions = useMemo(
    () => allItems.filter(i => i.item_type !== 'Service').map(i => ({ value: i.id, label: `${i.unique_id} - ${i.item_name}`, searchStr: `${i.unique_id} ${i.item_name}` })),
    [allItems]
  );

  const updateLine = (idx, field, value) => setLines(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  const removeLine = (idx) => setLines(prev => prev.filter((_, i) => i !== idx));
  const addLine = () => setLines(prev => [...prev, {
    _key: `new-${Date.now()}`, _isNew: true, item_id: '', item_name: '', unique_id: '', unit_of_measure: 'Nos',
    item_description: '', item_kind: 'item', is_service_item: false, quantity: '', rate: '', gst_percent: '0', delivery_date: '',
  }]);

  const selectItem = (idx, itemId) => {
    const item = allItems.find(i => i.id === itemId);
    if (!item) return;
    setLinkWarnings([]);
    setLines(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const next = {
        ...it, item_id: item.id, item_name: item.item_name, unique_id: item.unique_id || '',
        external_id: item.external_id || '', unit_of_measure: item.unit_of_measure || 'Nos', _editItem: false,
      };
      // Auto-fill CTS geometry from the new item's sheet master data (if it is a sheet).
      const hasSheet = item.sheet_thickness != null || item.sheet_length != null || item.sheet_grade;
      if (hasSheet) {
        if (item.sheet_grade) next.cts_grade = item.sheet_grade;
        if (item.sheet_thickness != null) next.cts_thickness = item.sheet_thickness;
        if (item.sheet_length != null && item.sheet_width != null) next.cts_sheet_size = `${item.sheet_length} x ${item.sheet_width}`;
        if (item.sheet_weight_kg != null) next.cts_unit_weight_kg = item.sheet_weight_kg;
      }
      return next;
    }));
  };

  const toggleCts = (idx) => setLines(prev => prev.map((it, i) => i === idx ? { ...it, cts_order: !it.cts_order } : it));
  const updateInstr = (idx, ci, field, value) => { setLinkWarnings([]); setLines(prev => prev.map((it, i) =>
    i === idx ? { ...it, cts_instructions: (it.cts_instructions || []).map((c, k) => k === ci ? { ...c, [field]: value } : c) } : it)); };
  const addInstr = (idx) => setLines(prev => prev.map((it, i) =>
    i === idx ? { ...it, cts_instructions: [...(it.cts_instructions || []), { cts_size: '', total_strips: '', sheets_needed: '', part_name: '' }] } : it));
  const removeInstr = (idx, ci) => setLines(prev => prev.map((it, i) =>
    i === idx ? { ...it, cts_instructions: (it.cts_instructions || []).filter((_, k) => k !== ci) } : it));

  // Auto-recalc strips/sheet, sheets needed, and weight from the (new) sheet geometry.
  const parseNums = (s) => (String(s || '').match(/[\d.]+/g) || []).map(parseFloat);
  const stripsPerSheet = (it, c) => {
    const [L, W] = parseNums(it.cts_sheet_size);
    const [sw, sl] = parseNums(c.cts_size);
    if (!sw || !L || !W) return 0;
    const other = sl ? (Math.abs(sl - W) <= Math.abs(sl - L) ? L : W) : Math.max(L, W);
    return Math.floor(other / sw);
  };
  const recalcLine = (idx) => setLines(prev => prev.map((it, i) => {
    if (i !== idx) return it;
    const [L, W] = parseNums(it.cts_sheet_size);
    const t = num(it.cts_thickness);
    let unitWt = num(it.cts_unit_weight_kg);
    if (L && W && t) unitWt = +(L * W * t * 7.86e-6).toFixed(3);
    const instrs = (it.cts_instructions || []).map(c => {
      const sps = stripsPerSheet(it, c);
      const demand = num(c.total_strips);
      const sheets = (sps > 0 && demand > 0) ? Math.ceil(demand / sps) : num(c.sheets_needed);
      return { ...c, sheets_needed: sheets };
    });
    const totalSheets = instrs.reduce((s, c) => s + (num(c.sheets_needed) || 0), 0);
    return {
      ...it, cts_instructions: instrs,
      cts_unit_weight_kg: unitWt || it.cts_unit_weight_kg,
      cts_total_sheets: totalSheets || it.cts_total_sheets,
      cts_total_weight_kg: (unitWt && totalSheets) ? +(unitWt * totalSheets).toFixed(3) : it.cts_total_weight_kg,
    };
  }));

  const grandTotal = lines.reduce((s, it) => s + lineTotal(it), 0);

  const save = async (force = false) => {
    // Basic validation
    for (const it of lines) {
      if (!it.item_id) { toast.error('Every line must have an item selected.'); return; }
      if (num(it.quantity) <= 0) { toast.error(`Quantity must be greater than 0 for ${it.item_name || 'item'}.`); return; }
    }
    if (lines.length === 0) { toast.error('A PO must have at least one line item.'); return; }

    // Reco pre-check: warn if any CTS cut size has no linked CTS item in a Cutting Plan.
    if (!force) {
      setSaving(true);
      try {
        const warns = [];
        for (const it of lines) {
          if (!it.cts_order || !it.item_id) continue;
          const sizes = (it.cts_instructions || []).map(c => c.cts_size).filter(s => String(s).trim() !== '');
          if (!sizes.length) continue;
          const res = await purchaseAPI.ctsLinkCheck({ item_id: it.item_id, cut_sizes: sizes });
          const unlinked = res.data.results.filter(r => !r.linked).map(r => r.cts_size);
          if (unlinked.length) warns.push({ item: `${it.unique_id || ''} ${it.item_name || ''}`.trim(), sizes: unlinked });
        }
        if (warns.length) { setLinkWarnings(warns); setSaving(false); return; }
      } catch (e) { /* non-blocking: allow save even if check fails */ }
    }

    setLinkWarnings([]);
    setSaving(true);
    try {
      const cnum = (v) => (v === '' || v == null) ? null : (isNaN(parseFloat(v)) ? v : parseFloat(v));
      const payload = {
        items: lines.map(it => {
          const line = {
            ...it,
            quantity: num(it.quantity),
            rate: num(it.rate),
            gst_percent: num(it.gst_percent),
          };
          delete line._editItem; delete line._isNew; delete line._key;
          if (it.cts_order) {
            line.cts_thickness = cnum(it.cts_thickness);
            line.cts_unit_weight_kg = cnum(it.cts_unit_weight_kg);
            line.cts_total_sheets = cnum(it.cts_total_sheets);
            line.cts_total_weight_kg = cnum(it.cts_total_weight_kg);
            line.cts_instructions = (it.cts_instructions || [])
              .filter(c => String(c.cts_size).trim() !== '')
              .map(c => ({ ...c, total_strips: cnum(c.total_strips), sheets_needed: cnum(c.sheets_needed) }));
          }
          return line;
        }),
        common_terms: terms,
        supplier_id: pr.supplier_id,
        supplier_name: pr.supplier_name,
        edit_reason: reason,
      };
      const res = await purchaseAPI.editPurchaseRequest(pr.id, payload);
      toast.success('Purchase order updated');
      onSaved && onSaved(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!pr} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="pr-edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm">
            Edit {pr?.pr_number}{pr?.po_number ? ` / ${pr.po_number}` : ''}{pr?.supplier_name ? ` — ${pr.supplier_name}` : ''}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded border border-amber-300 bg-amber-50/70 p-2 text-[11px] text-amber-800 flex items-start gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Editing an already-sent PO amends it. If any rate now exceeds the last purchase price it will be flagged for admin re-approval. All edits are recorded in the audit trail.</span>
          </div>

          {/* Line items */}
          <div className="space-y-2">
            {lines.map((it, idx) => (
              <div key={it._key} className="border rounded p-2.5 space-y-2 bg-muted/20" data-testid={`edit-line-${idx}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-muted-foreground">Line {idx + 1}</span>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1 text-[10px] cursor-pointer" data-testid={`edit-cts-toggle-${idx}`}>
                      <input type="checkbox" checked={!!it.cts_order} onChange={() => toggleCts(idx)} className="h-3 w-3" />
                      Cut-to-size line
                    </label>
                    <Button size="sm" variant="ghost" className="h-6 text-red-500" onClick={() => removeLine(idx)} data-testid={`edit-remove-line-${idx}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div className="col-span-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-[10px]">Item</Label>
                      {!it._isNew && !it._editItem && (
                        <button type="button" className="text-[10px] text-indigo-600 hover:underline"
                          onClick={() => updateLine(idx, '_editItem', true)} data-testid={`edit-change-item-${idx}`}>
                          Change item
                        </button>
                      )}
                    </div>
                    {(it._isNew || it._editItem) ? (
                      <SearchableSelect options={itemOptions} value={it.item_id} onSelect={(v) => selectItem(idx, v)}
                        placeholder="Search item..." emptyText="No items" testId={`edit-item-search-${idx}`} />
                    ) : (
                      <Input value={`${it.unique_id || ''} - ${it.item_name || ''}`} readOnly className="h-8 text-xs bg-muted" data-testid={`edit-item-name-${idx}`} />
                    )}
                  </div>
                  <div>
                    <Label className="text-[10px]">Quantity ({it.unit_of_measure || 'Nos'})</Label>
                    <Input type="number" step="0.01" value={it.quantity} onChange={e => updateLine(idx, 'quantity', e.target.value)}
                      className="h-8 text-xs" data-testid={`edit-qty-${idx}`} />
                  </div>
                  <div>
                    <Label className="text-[10px]">Rate {num(it.cts_unit_weight_kg) > 0 ? '(Rs/Kg)' : ''}</Label>
                    <Input type="number" step="0.01" value={it.rate} onChange={e => updateLine(idx, 'rate', e.target.value)}
                      className="h-8 text-xs" data-testid={`edit-rate-${idx}`} />
                  </div>
                  <div>
                    <Label className="text-[10px]">GST %</Label>
                    <Input type="number" step="0.01" value={it.gst_percent} onChange={e => updateLine(idx, 'gst_percent', e.target.value)}
                      className="h-8 text-xs" data-testid={`edit-gst-${idx}`} />
                  </div>
                  <div>
                    <Label className="text-[10px]">Delivery date</Label>
                    <Input value={it.delivery_date || ''} onChange={e => updateLine(idx, 'delivery_date', e.target.value)}
                      placeholder="e.g. 15 Jul" className="h-8 text-xs" data-testid={`edit-delivery-${idx}`} />
                  </div>
                  <div className="col-span-2 md:col-span-3">
                    <Label className="text-[10px]">Description / note</Label>
                    <Input value={it.item_description || ''} onChange={e => updateLine(idx, 'item_description', e.target.value)}
                      className="h-8 text-xs" data-testid={`edit-desc-${idx}`} />
                  </div>
                </div>

                {it.cts_order && (
                  <div className="rounded border border-amber-300 bg-amber-50/50 p-2 space-y-2" data-testid={`edit-cts-panel-${idx}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] font-semibold text-amber-800 flex items-center gap-1">✂ Cut-to-Size instructions</p>
                      <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => recalcLine(idx)} data-testid={`edit-cts-recalc-${idx}`}>
                        <RefreshCw className="w-3 h-3 mr-1" /> Recalc from sheet size
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                      <div>
                        <Label className="text-[10px]">Grade</Label>
                        <Input value={it.cts_grade || ''} onChange={e => updateLine(idx, 'cts_grade', e.target.value)} className="h-7 text-xs" data-testid={`edit-cts-grade-${idx}`} />
                      </div>
                      <div>
                        <Label className="text-[10px]">Thickness (mm)</Label>
                        <Input type="number" step="0.01" value={it.cts_thickness ?? ''} onChange={e => updateLine(idx, 'cts_thickness', e.target.value)} className="h-7 text-xs" data-testid={`edit-cts-thickness-${idx}`} />
                      </div>
                      <div>
                        <Label className="text-[10px]">Sheet size (L×W)</Label>
                        <Input value={it.cts_sheet_size || ''} onChange={e => updateLine(idx, 'cts_sheet_size', e.target.value)} placeholder="2500 x 1500" className="h-7 text-xs" data-testid={`edit-cts-sheet-size-${idx}`} />
                      </div>
                      <div>
                        <Label className="text-[10px]">Weight (Kg/sheet)</Label>
                        <Input type="number" step="0.001" value={it.cts_unit_weight_kg ?? ''} onChange={e => updateLine(idx, 'cts_unit_weight_kg', e.target.value)} className="h-7 text-xs" data-testid={`edit-cts-unit-weight-${idx}`} />
                      </div>
                      <div>
                        <Label className="text-[10px]">Full sheets</Label>
                        <Input type="number" step="1" value={it.cts_total_sheets ?? ''} onChange={e => updateLine(idx, 'cts_total_sheets', e.target.value)} className="h-7 text-xs" data-testid={`edit-cts-total-sheets-${idx}`} />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="grid grid-cols-12 gap-1 text-[9px] font-semibold text-muted-foreground px-1">
                        <span className="col-span-4">Cut size (mm)</span>
                        <span className="col-span-2">Strips</span>
                        <span className="col-span-2">Sheets</span>
                        <span className="col-span-3">Part / for</span>
                        <span className="col-span-1"></span>
                      </div>
                      {(it.cts_instructions || []).map((c, ci) => (
                        <div key={ci} className="grid grid-cols-12 gap-1 items-center" data-testid={`edit-cts-instr-${idx}-${ci}`}>
                          <Input value={c.cts_size ?? ''} onChange={e => updateInstr(idx, ci, 'cts_size', e.target.value)} placeholder="105 x 1500" className="col-span-4 h-7 text-xs" data-testid={`edit-cts-instr-size-${idx}-${ci}`} />
                          <Input type="number" value={c.total_strips ?? ''} onChange={e => updateInstr(idx, ci, 'total_strips', e.target.value)} className="col-span-2 h-7 text-xs" data-testid={`edit-cts-instr-strips-${idx}-${ci}`} />
                          <Input type="number" value={c.sheets_needed ?? ''} onChange={e => updateInstr(idx, ci, 'sheets_needed', e.target.value)} className="col-span-2 h-7 text-xs" />
                          <Input value={c.part_name ?? ''} onChange={e => updateInstr(idx, ci, 'part_name', e.target.value)} className="col-span-3 h-7 text-xs" />
                          <Button size="sm" variant="ghost" className="col-span-1 h-7 w-7 p-0 text-red-500" onClick={() => removeInstr(idx, ci)} data-testid={`edit-cts-instr-remove-${idx}-${ci}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                          {stripsPerSheet(it, c) > 0 && (
                            <span className="col-span-12 text-[9px] text-amber-700 pl-1" data-testid={`edit-cts-instr-hint-${idx}-${ci}`}>
                              ≈ {stripsPerSheet(it, c)} strips/sheet from this sheet size
                            </span>
                          )}
                        </div>
                      ))}
                      <Button size="sm" variant="outline" className="h-6 text-[10px] mt-0.5" onClick={() => addInstr(idx)} data-testid={`edit-cts-add-instr-${idx}`}>
                        <Plus className="w-3 h-3 mr-0.5" /> Add cut size
                      </Button>
                    </div>
                    <div>
                      <Label className="text-[10px]">CTS note</Label>
                      <Input value={it.cts_note || ''} onChange={e => updateLine(idx, 'cts_note', e.target.value)} className="h-7 text-xs" data-testid={`edit-cts-note-${idx}`} />
                    </div>
                    <p className="text-[9px] text-amber-700">Note: each cut size must have a matching CTS stock item linked in the part's Cutting Plan (BOM) for reconciliation to work.</p>
                  </div>
                )}
                <div className="text-right text-[11px] text-muted-foreground">
                  Line total: <span className="font-semibold text-foreground">Rs {lineTotal(it).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              </div>
            ))}
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={addLine} data-testid="edit-add-line-btn">
              <Plus className="w-3 h-3 mr-1" /> Add Line
            </Button>
          </div>

          {/* Common terms */}
          <div className="border rounded p-3 space-y-2 bg-muted/10">
            <p className="text-[11px] font-semibold text-muted-foreground">Common Terms</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px]">Delivery terms</Label>
                <Input value={terms.delivery_terms} onChange={e => setTerms({ ...terms, delivery_terms: e.target.value })} className="h-8 text-xs" data-testid="edit-common-delivery" />
              </div>
              <div>
                <Label className="text-[10px]">Payment terms</Label>
                <Input value={terms.payment_terms} onChange={e => setTerms({ ...terms, payment_terms: e.target.value })} className="h-8 text-xs" data-testid="edit-common-payment" />
              </div>
              <div>
                <Label className="text-[10px]">Documents required</Label>
                <Input value={terms.documents_required} onChange={e => setTerms({ ...terms, documents_required: e.target.value })} className="h-8 text-xs" data-testid="edit-common-docs" />
              </div>
              <div>
                <Label className="text-[10px]">Special instructions</Label>
                <Input value={terms.special_terms} onChange={e => setTerms({ ...terms, special_terms: e.target.value })} className="h-8 text-xs" data-testid="edit-common-special" />
              </div>
            </div>
          </div>

          <div>
            <Label className="text-[10px]">Reason for edit (recorded in audit trail)</Label>
            <Textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} className="text-xs" placeholder="e.g. Supplier confirmed revised rate / amended quantity" data-testid="edit-reason" />
          </div>

          <div className="flex items-center justify-end gap-2 text-xs">
            <span className="text-muted-foreground">New PO value (before tax):</span>
            <span className="text-base font-bold" data-testid="edit-grand-total">Rs {grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>

          {linkWarnings.length > 0 && (
            <div className="rounded border border-rose-300 bg-rose-50 p-2.5 text-[11px] text-rose-800 space-y-1" data-testid="cts-link-warning">
              <p className="font-semibold flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Some cut sizes are not linked to a CTS stock item</p>
              <p>Reconciliation will fail for these until a CTS item is linked in the part's Cutting Plan (BOM):</p>
              <ul className="list-disc list-inside">
                {linkWarnings.map((w, i) => <li key={i}><strong>{w.item}</strong>: {w.sizes.join(', ')}</li>)}
              </ul>
              <p className="text-rose-600">You can still save the amendment and link them later.</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="edit-cancel-btn">Cancel</Button>
          {linkWarnings.length > 0 ? (
            <Button size="sm" variant="destructive" onClick={() => save(true)} disabled={saving} data-testid="edit-save-anyway-btn">
              {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />} Save anyway
            </Button>
          ) : (
            <Button size="sm" onClick={() => save(false)} disabled={saving} data-testid="edit-save-btn">
              {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />} Save Changes
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
